/**
 * Configuration Puck pour le contenu des pages web — ASSEMBLEUR.
 * La config de chaque bloc (label, fields, defaultProps, render) vit à côté
 * de son composant : blocks/<famille>/<Bloc>/<Bloc>.puck.jsx
 *
 * Deux familles de blocs :
 *   - "Contenu proposal" : générés automatiquement par le parser
 *   - "Blocs Web"        : composants Mantine insérés manuellement
 */

import React from 'react';
import proposalSectionConfig from '../blocks/proposal/ProposalSection/ProposalSection.puck';
import proposalFreeLayoutConfig from '../blocks/proposal/ProposalFreeLayout/ProposalFreeLayout.puck';
import proposalRichTextConfig from '../blocks/proposal/ProposalRichText/ProposalRichText.puck';
import proposalTitleConfig from '../blocks/proposal/ProposalTitle/ProposalTitle.puck';
import proposalImageConfig from '../blocks/proposal/ProposalImage/ProposalImage.puck';
import proposalShapeConfig from '../blocks/proposal/ProposalShape/ProposalShape.puck';
import proposalCheckboxConfig from '../blocks/proposal/ProposalCheckbox/ProposalCheckbox.puck';
import proposalSelectorConfig from '../blocks/proposal/ProposalSelector/ProposalSelector.puck';
import carouselBlockConfig from '../blocks/web/CarouselBlock/CarouselBlock.puck';
import infoCardConfig from '../blocks/web/InfoCard/InfoCard.puck';
import accordionPanelConfig from '../blocks/web/AccordionPanel/AccordionPanel.puck';
import timelineBlockConfig from '../blocks/web/TimelineBlock/TimelineBlock.puck';
import sectionDividerConfig from '../blocks/web/SectionDivider/SectionDivider.puck';
import scrollStoryConfig from '../blocks/web/ScrollStoryBlock/ScrollStoryBlock.puck';
import hotspotImageConfig from '../blocks/web/HotspotImageBlock/HotspotImageBlock.puck';
import imageComparisonConfig from '../blocks/web/ImageComparisonBlock/ImageComparisonBlock.puck';
import videoBlockConfig from '../blocks/web/VideoBlock/VideoBlock.puck';
import buttonBlockConfig from '../blocks/web/ButtonBlock/ButtonBlock.puck';
import tabsBlockConfig from '../blocks/web/TabsBlock/TabsBlock.puck';
import embedBlockConfig from '../blocks/web/EmbedBlock/EmbedBlock.puck';
import {
  heroMacroConfig, actionBannerMacroConfig,
  keyFiguresMacroConfig, faqMacroConfig, logoStripMacroConfig,
  clipboardSectionTileConfig, clipboardBoxTileConfig,
  gridPresetConfigs, sharedCompositionConfigs,
} from './layoutMacros';
import spacerConfig from '../blocks/web/Spacer/Spacer.puck';
import editableDataTableConfig from '../blocks/web/EditableDataTable/EditableDataTable.puck';
import { DropZone } from '@puckeditor/core';
import { withPageContentMaxWidth } from '../layout/withPageContentMaxWidth';
import { withBoxActions } from './withBoxActions';
import { CLIPBOARD_BOX_TILES, CLIPBOARD_SECTION_TILES } from './blockClipboard';
import PageRoot from '../layout/PageRoot';
import { puckColorField } from './fields/createPuckColorField';
import { DEFAULT_PAGE_BACKGROUND } from './pagePuckData';
import { COMPOSITION_TILE_TYPES, GRID_PRESETS } from './compositionLayouts';

// ─── Config Puck ──────────────────────────────────────────────────────────────

const puckContentConfigBase = {
  categories: {
    // Section et Mise en page libre ne se draguent pas : la Section se crée
    // automatiquement au drop d'un bloc contenu ; la Mise en page libre n'est
    // générée que par l'import du proposal. Tous deux restent réglables une
    // fois en page (outline / sélection).
    'Contenu': {
      components: ['ProposalRichText', 'ProposalTitle', 'ProposalImage', 'ImageComparisonBlock', 'HotspotImageBlock', 'VideoBlock', 'EmbedBlock', 'ButtonBlock'],
    },
    // Onglet Blocs : chaque tuile pose un Bloc entier, déjà disposé — pas une
    // box simple. Les macros portent du contenu de démonstration, les
    // compositions du catalogue partagé posent des box vides.
    // Les grilles d'abord : poser une structure vide, puis la remplir, est le
    // geste le plus courant. Ce sont les mêmes que dans « Disposition ».
    'Grilles': {
      components: GRID_PRESETS.map(({ type }) => type),
    },
    'Compositions': {
      components: COMPOSITION_TILE_TYPES.filter(
        (type) => !GRID_PRESETS.some((preset) => preset.type === type),
      ),
    },
    'Données': {
      components: ['EditableDataTable', 'InfoCard', 'AccordionPanel', 'TimelineBlock', 'ScrollStoryBlock', 'CarouselBlock'],
    },
    'Mise en page': {
      components: ['TabsBlock', 'SectionDivider', 'Spacer'],
    },
    // Blocs hors palette (émis par le parser uniquement, ex. ProposalShape,
    // ProposalCheckbox) : Puck range les composants non catégorisés dans
    // « other » — masquée.
    other: { visible: false },
  },

  components: {
    // ══ BLOCS PROPOSAL ════════════════════════════════════════════════════════
    // ProposalColumn : rendu legacy uniquement (import proposal ancien format).
    // Non listé dans la palette — utiliser Section + sectionColumns.

    ProposalSection: proposalSectionConfig,
    ProposalFreeLayout: proposalFreeLayoutConfig,
    ProposalRichText: proposalRichTextConfig,
    ProposalTitle: proposalTitleConfig,
    ProposalImage: proposalImageConfig,
    ProposalShape: proposalShapeConfig,
    ProposalCheckbox: proposalCheckboxConfig,
    ProposalSelector: proposalSelectorConfig,
    EditableDataTable: editableDataTableConfig,

    // ══ BLOCS WEB MANTINE ═════════════════════════════════════════════════════

    CarouselBlock: carouselBlockConfig,
    HeroBlock: heroMacroConfig,
    ActionBannerBlock: actionBannerMacroConfig,
    InfoCard: infoCardConfig,
    KeyFiguresBlock: keyFiguresMacroConfig,
    FaqBlock: faqMacroConfig,
    LogoStripBlock: logoStripMacroConfig,
    ...gridPresetConfigs,
    ...sharedCompositionConfigs,
    // Tuiles du presse-papier, une par place : hors catégorie, la palette les
    // rend elle-même quand une copie attend (blockClipboard).
    ...Object.fromEntries(CLIPBOARD_SECTION_TILES.map((type) => [type, clipboardSectionTileConfig])),
    ...Object.fromEntries(CLIPBOARD_BOX_TILES.map((type) => [type, clipboardBoxTileConfig])),
    AccordionPanel: accordionPanelConfig,
    TimelineBlock: timelineBlockConfig,
    SectionDivider: sectionDividerConfig,
    Spacer: spacerConfig,
    VideoBlock: videoBlockConfig,
    ImageComparisonBlock: imageComparisonConfig,
    HotspotImageBlock: hotspotImageConfig,
    ScrollStoryBlock: scrollStoryConfig,
    EmbedBlock: embedBlockConfig,
    ButtonBlock: buttonBlockConfig,
    TabsBlock: tabsBlockConfig,
  },
};

export const puckContentConfig = {
  ...puckContentConfigBase,
  root: {
    label: 'Page',
    render: ({ children, backgroundColor, puck }) => (
      <PageRoot backgroundColor={backgroundColor}>
        {puck?.isEditing ? (
          // Zone racine explicite (pas de disallow : le drop d'un bloc
          // contenu y est permis, wrappé en Section via onAction — cf.
          // PuckPageEditor). collisionAxis="y" limite le hit-test Puck à
          // l'axe vertical pour réduire le flicker root/Section en bord de
          // zone (cf. mémoire webviewer-perf-fix-invariants pour le contexte
          // du chantier drop-zone). Viewer : children, chemin intact.
          <DropZone zone="default-zone" collisionAxis="y" />
        ) : children}
      </PageRoot>
    ),
    defaultProps: {
      title: '',
      backgroundColor: DEFAULT_PAGE_BACKGROUND,
    },
    fields: {
      title: { type: 'text', label: 'Titre de la page' },
      backgroundColor: puckColorField('Couleur de fond'),
    },
  },
  components: withBoxActions(withPageContentMaxWidth(puckContentConfigBase.components)),
};
